import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import TextSkeleton from '../../../skeletons/text-skeleton';
import ButtonSkeleton from '../../../skeletons/button-skeleton';
import Heading from '../../../heading';
import Paragraph from '../../../paragraph';
import ActionButton from '../../../action-button';
import InfoWithPopover from '../../../info-with-popover';
import HorizontalStack from '../../../horizontal-stack';
import {
  APPEARANCES as BUTTON_APPEARANCES,
  FORMATS as BUTTON_FORMATS,
} from '../../../action-button/constants';
import { HEADING_APPEARANCES } from '../../../heading/constants';
import useMatchMDBreakpoint from '../../../../hooks/use-match-md-breakpoint';

const DomainSelectorListRow = ({
  captions,
  disabled = false,
  onClick = Function.prototype,
  isLoading = false,
  buttonAppearance = BUTTON_APPEARANCES.default,
  buttonFormat = BUTTON_FORMATS.solid,
  priceAppearance = HEADING_APPEARANCES.default,
  isSticky = false,
}) => {
  const isMd = useMatchMDBreakpoint();

  const withOldPrice = Boolean(captions.oldPrice);
  const withPrice = Boolean(captions.price);
  const listRowClasses = classNames('ws-ui-domain-selector-list-row', {
    'ws-ui-domain-selector-list-row--loading': isLoading,
    'ws-ui-domain-selector-list-row--disabled': disabled,
    'ws-ui-domain-selector-list-row--sticky': isSticky,
  });

  return (
    <div className={listRowClasses}>
      <div className="ws-ui-domain-selector-list-row__container">
        <div className="ws-ui-domain-selector-list-row__title">
          <Heading type={isMd ? 'heading-5' : 'heading-6'}>
            {isLoading ? <TextSkeleton width="211px" /> : captions.title}
          </Heading>
          {!isLoading && captions.description && (
            <Paragraph size="small">{captions.description}</Paragraph>
          )}
        </div>
        <div className="ws-ui-domain-selector-list-row__price-container">
          {isLoading && (
            <div className="ws-ui-domain-selector-list-row__price">
              <TextSkeleton width="44px" />
            </div>
          )}
          {!isLoading && withPrice && (
            <React.Fragment>
              <div className="ws-ui-domain-selector-list-row__price-wrapper">
                <HorizontalStack
                  breakpoints={{ md: { gap: 'spacing-3-xs' } }}
                  verticalAlign="center"
                >
                  {withOldPrice && (
                    <div className="ws-ui-domain-selector-list-row__old-price">
                      <Paragraph {...(!isMd && { size: 'small' })}>
                        <s>{captions.oldPrice}</s>
                      </Paragraph>
                    </div>
                  )}
                  <HorizontalStack gap="spacing-3-xs" verticalAlign="center">
                    <div className="ws-ui-domain-selector-list-row__price">
                      <Heading
                        appearance={priceAppearance}
                        type={isMd && !disabled ? 'heading-5' : 'heading-6'}
                        singleLine
                      >
                        {disabled ? captions.price : <b>{captions.price}</b>}
                      </Heading>
                    </div>
                    {!isMd && captions.popoverTitle && (
                      <InfoWithPopover
                        title={captions.popoverTitle}
                        appearance="dark"
                        placement="bottom-center"
                        mode="popover"
                      />
                    )}
                  </HorizontalStack>
                </HorizontalStack>
              </div>
              {isMd && captions.priceDescription && (
                <div className="ws-ui-domain-selector-list-row__price-description">
                  <Paragraph size="small">{captions.priceDescription}</Paragraph>{' '}
                  {captions.popoverTitle && (
                    <InfoWithPopover
                      title={captions.popoverTitle}
                      appearance="dark"
                      placement="bottom-center"
                    />
                  )}
                </div>
              )}
            </React.Fragment>
          )}
        </div>
      </div>
      <div className="ws-ui-domain-selector-list-row__button">
        {isLoading ? (
          <ButtonSkeleton />
        ) : (
          !disabled && (
            <ActionButton
              label={captions.button}
              appearance={buttonAppearance}
              format={buttonFormat}
              size="large"
              onClick={onClick}
            />
          )
        )}
      </div>
    </div>
  );
};

DomainSelectorListRow.propTypes = {
  captions: PropTypes.shape({
    title: PropTypes.string,
    description: PropTypes.string,
    price: PropTypes.string,
    oldPrice: PropTypes.string,
    button: PropTypes.string,
    priceDescription: PropTypes.string,
    popoverTitle: PropTypes.string,
  }).isRequired,
  disabled: PropTypes.bool,
  isLoading: PropTypes.bool,
  buttonAppearance: PropTypes.oneOf(Object.values(BUTTON_APPEARANCES)),
  buttonFormat: PropTypes.oneOf(Object.values(BUTTON_FORMATS)),
  priceAppearance: PropTypes.oneOf(Object.values(HEADING_APPEARANCES)),
  isSticky: PropTypes.bool,
  onClick: PropTypes.func,
};

export default DomainSelectorListRow;
